import type { Messages } from './messages';

/**
 * The API's enums, named in Arabic.
 *
 * Every key here is `<family>.<VALUE AS THE API SENDS IT>`, so `lib/format.ts`
 * resolves a label by concatenation and the compiler checks the whole family
 * exists. Add a value to an API enum and the label function stops compiling
 * until it is named here — the alternative is `VIEWER` in Latin in the middle
 * of an Arabic table.
 *
 * Open-ended families (audit actions, tenant permission codes, template keys)
 * are looked up with `hasMessage` and fall back to the raw value: an unlabelled
 * row in an audit trail is still evidence, and a blank would not be.
 *
 * Owned by lib. A page that meets a value not named here adds it here.
 */
export const vocabulary = {
  /* A member's role inside a المنشأة (ATA-30). */
  'role.USER': 'مستخدم',
  'role.MODEL_SUPERVISOR': 'مشرف النماذج',
  'role.GENERAL_ADMIN': 'المشرف العام',

  /* A member's account status. INVITED is "no password chosen yet". */
  'memberStatus.INVITED': 'بانتظار التفعيل',
  'memberStatus.ACTIVE': 'نشط',
  'memberStatus.SUSPENDED': 'موقوف',

  /* A المنشأة is on or off; there is no third state. */
  'tenantStatus.active': 'مفعّلة',
  'tenantStatus.suspended': 'موقوفة',

  /* An operator's rank. Not a tenant role; the two never mix. */
  'operatorRole.SUPPORT': 'دعم',
  'operatorRole.OPERATOR': 'مشغّل',
  'operatorRole.SUPERUSER': 'مسؤول المنصّة',

  'platformPermission.PLATFORM_TENANT_READ': 'عرض المنشآت',
  'platformPermission.PLATFORM_TENANT_CREATE': 'إضافة منشأة',
  'platformPermission.PLATFORM_TENANT_MANAGE': 'تعديل المنشآت',
  'platformPermission.PLATFORM_TENANT_USERS_READ': 'عرض حسابات المنشآت',
  'platformPermission.PLATFORM_OPERATOR_MANAGE': 'إدارة مشغّلي المنصّة',
  'platformPermission.PLATFORM_AUDIT_READ': 'قراءة سجل المنصّة',

  /* The tenant permission catalogue, `src/modules/policy/permissions.ts`. */
  'permission.TENANT_ADMINISTER': 'إدارة المنشأة',
  'permission.USER_MANAGE': 'إدارة الأعضاء',
  'permission.MODEL_APPROVE': 'اعتماد النماذج',
  'permission.AUDIT_READ': 'قراءة سجل التدقيق',
  'permission.BILLING_MANAGE': 'إدارة التكاليف',
  'permission.DOCUMENT_CREATE': 'إنشاء المستندات',
  'permission.CONVERSATION_CREATE': 'بدء المحادثات',
  'permission.KNOWLEDGE_MANAGE': 'إدارة قاعدة المعرفة',
  'permission.CASE_VIEW': 'عرض القضايا',
  'permission.CASE_CREATE': 'إنشاء القضايا',
  'permission.CASE_EDIT': 'تعديل القضايا',
  'permission.CASE_ARCHIVE': 'أرشفة القضايا',
  'permission.CONVERSATION_REVIEW': 'مراجعة المحادثات',

  'joinStatus.PENDING': 'بانتظار البتّ',
  'joinStatus.APPROVED': 'مقبول',
  'joinStatus.REJECTED': 'مرفوض',

  /* SENT means a relay answered 250. It does not mean delivered. */
  'dispatchStatus.QUEUED': 'في الطابور',
  'dispatchStatus.SENT': 'قبِلها المُرحِّل',
  'dispatchStatus.FAILED': 'فشلت',

  'templateCategory.account': 'الحساب',
  'templateCategory.security': 'الأمان',
  'templateCategory.cost': 'الكلفة',
  'templateCategory.sharing': 'المشاركة',
  'templateCategory.governance': 'الحوكمة',

  /* `GET /models` — `tier` and `costClass`. Relative, deliberately not numbers. */
  'modelTier.very_high': 'عالية جدًا',
  'modelTier.high': 'عالية',
  'modelTier.good': 'جيدة',
  'modelCost.high': 'مرتفعة',
  'modelCost.medium': 'متوسطة',
  'modelCost.low': 'منخفضة',

  /* `GET /insights` → attention[].kind. */
  'attention.DOCUMENTS_FAILED': 'مستندات فشلت معالجتها',
  'attention.SPEND_NEAR_CAP': 'الإنفاق يقترب من السقف',
  'attention.NO_GENERAL_ADMIN': 'لا مشرف عام نشط',
  'attention.JOIN_REQUESTS_WAITING': 'طلبات انضمام بانتظار البتّ',

  /* `GET /tenants/:id/content-summary` — the enums its counts are keyed by. Labels, never contents. */
  'documentStatus.UPLOADED': 'مرفوع',
  'documentStatus.EXTRACTING': 'قيد الاستخراج',
  'documentStatus.NO_TEXT_LAYER': 'بلا طبقة نصية',
  'documentStatus.EXTRACTED': 'استُخرج نصه',
  'documentStatus.CHUNKED': 'قُسِّم',
  'documentStatus.EMBEDDED': 'حُسبت متجهاته',
  'documentStatus.INDEXED': 'مفهرس',
  'documentStatus.FAILED': 'فشلت معالجته',
  'documentStatus.ARCHIVED': 'مؤرشف',

  /* ATA-71 sensitivity levels. */
  'sensitivity.PUBLIC': 'عام',
  'sensitivity.INTERNAL': 'داخلي',
  'sensitivity.CONFIDENTIAL': 'سري',
  'sensitivity.HIGHLY_CONFIDENTIAL': 'سري للغاية',

  'visibility.PRIVATE': 'لصاحبه وحده',
  'visibility.RESTRICTED': 'لصاحبه ولأشخاص محدّدين',
  'visibility.TENANT': 'لكل أعضاء المنشأة',

  'documentKind.FILE': 'ملف مرفوع',
  'documentKind.TEXT': 'نص ملصق',
  'documentKind.LINK': 'صفحة ويب',

  'messageRole.USER': 'أسئلة الأعضاء',
  'messageRole.ASSISTANT': 'إجابات النماذج',
  'messageRole.SYSTEM': 'رسائل النظام',

  /* ATA-150: how a message ended. */
  'messageStatus.COMPLETE': 'مكتملة',
  'messageStatus.TRUNCATED': 'بلغت حدّ الطول',
  'messageStatus.INTERRUPTED': 'انقطع بثّها',
  'messageStatus.FAILED': 'فشلت أو رُفضت',

  'filePurpose.KNOWLEDGE': 'مرفوعات المعرفة',
  'filePurpose.ASSET': 'أصول المنشأة (شعار وصور)',

  'fileStatus.PENDING': 'رفع لم يُؤكَّد',
  'fileStatus.READY': 'جاهز',
  'fileStatus.OTHER': 'حالة أخرى',

  'sourceType.UPLOAD': 'رفع ملفات',
  'sourceType.CONVERSATION': 'من محادثة',
  'sourceType.WEB': 'من الويب',
  'sourceType.TEXT': 'نص مباشر',
  'sourceType.CONNECTOR': 'موصِّل',

  'importStatus.AWAITING_APPROVAL': 'بانتظار الموافقة',
  'importStatus.QUEUED': 'في الطابور',
  'importStatus.RUNNING': 'قيد التنفيذ',
  'importStatus.COMPLETED': 'اكتمل',
  'importStatus.COMPLETED_WITH_FAILURES': 'اكتمل مع إخفاقات',
  'importStatus.CANCELLED': 'أُلغي',

  /* Every account row, join requests and refusals included — wider than `memberStatus`. */
  'accountStatus.ACTIVE': 'نشط',
  'accountStatus.INVITED': 'بانتظار التفعيل',
  'accountStatus.SUSPENDED': 'موقوف',
  'accountStatus.PENDING': 'طلب انضمام',
  'accountStatus.REJECTED': 'طلب مرفوض',

  'ingestionStage.FETCH': 'الجلب',
  'ingestionStage.EXTRACT': 'الاستخراج',
  'ingestionStage.NORMALISE': 'التوحيد',
  'ingestionStage.STRUCTURE': 'الهيكلة',
  'ingestionStage.CHUNK': 'التقسيم',
  'ingestionStage.EMBED': 'حساب المتجهات',
  'ingestionStage.INDEX': 'الفهرسة',

  /* Company names: written as the companies write them. */
  'provider.ANTHROPIC': 'Anthropic',
  'provider.OPENAI': 'OpenAI',

  /* Audit actions — the operator trail. */
  'action.platform.signed_in': 'دخول مشغّل',
  'action.platform.sign_in_failed': 'محاولة دخول فاشلة',
  'action.platform.signed_out': 'خروج مشغّل',
  'action.platform.tenant_created': 'إنشاء منشأة',
  'action.platform.tenant_updated': 'تعديل منشأة',
  'action.platform.tenant_deactivated': 'إيقاف منشأة',
  'action.platform.tenant_reactivated': 'إعادة تفعيل منشأة',
  'action.platform.operator_created': 'إنشاء حساب تشغيل',
  'action.platform.operator_updated': 'تعديل حساب تشغيل',
  'action.platform.identity_provider_updated': 'تعديل الدخول عبر Microsoft',
  'action.platform.identity_provider_enabled': 'تفعيل الدخول عبر Microsoft',
  'action.platform.identity_provider_disabled': 'تعطيل الدخول عبر Microsoft',
  'action.platform.mail_settings_enabled': 'تفعيل بريد المنشأة',
  'action.platform.mail_settings_disabled': 'تعطيل بريد المنشأة',
  'action.platform.mail_settings_tested': 'إرسال بريد تجريبي',
  'action.platform.email_template_updated': 'تعديل قالب بريد',
  'action.platform.email_branding_updated': 'تعديل هوية البريد',
  'action.platform.frontend_url_updated': 'تعديل واجهة المنشأة',
  'action.platform.tenant_user_created': 'إنشاء حساب في منشأة',
  'action.platform.tenant_user_invited': 'إرسال دعوة',
  'action.platform.tenant_user_role_changed': 'تغيير دور عضو',
  'action.platform.tenant_user_suspended': 'إيقاف عضو',
  'action.platform.tenant_user_reactivated': 'إعادة تفعيل عضو',
  'action.platform.tenant_role_permissions_set': 'تعديل صلاحيات دور',
  'action.platform.tenant_join_approved': 'قبول طلب انضمام',
  'action.platform.tenant_join_rejected': 'رفض طلب انضمام',
  'action.platform.tenant_permission_granted': 'منح صلاحية لعضو',
  'action.platform.tenant_permission_revoked': 'سحب صلاحية من عضو',
  'action.platform.tenant_model_enabled': 'تفعيل نموذج لمنشأة',
  'action.platform.tenant_model_disabled': 'تعطيل نموذج لمنشأة',

  /* Audit actions — a المنشأة's own trail. */
  'action.user.created': 'إنشاء حساب',
  'action.user.updated': 'تعديل عضو',
  'action.user.role_changed': 'تغيير دور',
  'action.user.suspended': 'إيقاف عضو',
  'action.user.reactivated': 'إعادة تفعيل عضو',
  'action.user.password_changed': 'تغيير كلمة المرور',
  'action.user.password_change_failed': 'محاولة تغيير كلمة مرور فاشلة',
  'action.user.signed_in': 'تسجيل دخول',
  'action.user.sign_in_failed': 'محاولة دخول فاشلة',
  'action.user.signed_out': 'تسجيل خروج',
  'action.document.uploaded': 'رفع مستند',
  'action.document.read': 'فتح مستند',
  'action.document.deleted': 'حذف مستند',
  'action.document.visibility_changed': 'تغيير ظهور مستند',
  'action.document.grant_added': 'منح وصول لمستند',
  'action.document.grant_revoked': 'سحب وصول لمستند',
  'action.conversation.created': 'بدء محادثة',
  'action.conversation.reviewed': 'مراجعة محادثة',
  'action.tenant.updated': 'تعديل إعدادات المنشأة',
  'action.credential.rotated': 'تدوير بيانات اعتماد',
  'action.model.invoked': 'استدعاء نموذج',
} as const satisfies Messages<
  | 'role'
  | 'memberStatus'
  | 'tenantStatus'
  | 'operatorRole'
  | 'platformPermission'
  | 'permission'
  | 'joinStatus'
  | 'dispatchStatus'
  | 'templateCategory'
  | 'attention'
  | 'modelTier'
  | 'modelCost'
  | 'documentStatus'
  | 'sensitivity'
  | 'visibility'
  | 'documentKind'
  | 'messageRole'
  | 'messageStatus'
  | 'filePurpose'
  | 'fileStatus'
  | 'sourceType'
  | 'importStatus'
  | 'accountStatus'
  | 'ingestionStage'
  | 'provider'
  | 'action'
>;
